import React, { useState, useEffect } from 'react';
import Meta from './Meta';
import JsonLd from './JsonLd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, MapPin } from 'lucide-react';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { formatDateForDisplay } from '../utils/dateUtils';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getResolvedImageUrl, setResolvedImageUrl } from '../utils/imageCache';

interface Activity {
  id: string;
  title: string;
  date: string;
  content: string;
  imageUrl: string;
    location?: string;
}

interface NavActivity {
  id: string;
  title: string;
  imageUrl: string;
  date: any;
}

const ActivityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [prevActivity, setPrevActivity] = useState<NavActivity | null>(null);
  const [nextActivity, setNextActivity] = useState<NavActivity | null>(null);
  const storage = getStorage();
  const location = useLocation();

  const handleBack = () => {
    const state = (location && (location as any).state) as { from?: 'home' | 'activities-list' } | null;
    if (state?.from === 'activities-list') {
      navigate('/activities-detail');
      return;
    }
    navigate('/#activities', { state: { instant: true } });
  };

  useEffect(() => {
    const resolveImageUrl = async (path: string): Promise<string> => {
      try {
        if (!path) return 'https://via.placeholder.com/300';
        // Try cache first
        const cached = getResolvedImageUrl(path);
        if (cached) return cached;
        if (path.startsWith('http')) return path;
        const imageRef = ref(storage, path);
        const url = await getDownloadURL(imageRef);
        setResolvedImageUrl(path, url);
        return url;
      } catch (error) {
        console.error('Error getting download URL: ', error);
        return 'https://via.placeholder.com/300';
      }
    };

    const fetchAdjacent = async (currentDate: any) => {
      try {
        const activitiesCollection = collection(db, 'activities');

        // 前の記事（より古い = date が小さいものから近いものを1件）
        const prevQ = query(
          activitiesCollection,
          orderBy('date', 'desc'),
          where('date', '<', currentDate),
          limit(1)
        );
        const prevSnap = await getDocs(prevQ);
        if (!prevSnap.empty) {
          const d = prevSnap.docs[0];
          const data: any = d.data();
          const imageUrl = await resolveImageUrl(data.imageUrl);
          setPrevActivity({ id: d.id, title: data.title || '', imageUrl, date: data.date });
          // Preload prev image
          if (imageUrl) {
            const img = new Image();
            (img as any).decoding = 'async';
            img.src = imageUrl;
          }
        } else {
          setPrevActivity(null);
        }

        // 次の記事（より新しい = date が大きいものから近いものを1件）
        const nextQ = query(
          activitiesCollection,
          orderBy('date', 'asc'),
          where('date', '>', currentDate),
          limit(1)
        );
        const nextSnap = await getDocs(nextQ);
        if (!nextSnap.empty) {
          const d = nextSnap.docs[0];
          const data: any = d.data();
          const imageUrl = await resolveImageUrl(data.imageUrl);
          setNextActivity({ id: d.id, title: data.title || '', imageUrl, date: data.date });
          // Preload next image
          if (imageUrl) {
            const img = new Image();
            (img as any).decoding = 'async';
            img.src = imageUrl;
          }
        } else {
          setNextActivity(null);
        }
      } catch (e) {
        console.error('Failed to fetch adjacent activities', e);
      }
    };

    const fetchActivity = async () => {
      if (!id) return;
      setLoading(true);
      const docRef = doc(db, "activities", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const imageUrl = await resolveImageUrl(data.imageUrl);
        setActivity({
          id: docSnap.id,
          title: data.title,
          date: data.date,
          content: data.content,
          imageUrl: imageUrl,
          location: data.location || '',
        });

        // 前後の記事を取得
        await fetchAdjacent(data.date);
      } else {
        console.log("No such document!");
      }
      setLoading(false);
    };

    fetchActivity();
  }, [id, storage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white" style={{ marginTop: '80px' }}>
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center text-pink-500 hover:text-pink-600 transition-colors"
              >
                <ArrowLeft size={20} className="mr-2" />
                戻る
              </button>
              <h2 className="text-xl font-bold" style={{ color: '#e3628d' }}>
                活動詳細
              </h2>
              <div className="w-20"></div>
            </div>
          </div>
        </header>

        {/* Skeleton Content */}
        <div className="py-12">
          <div className="container mx-auto px-4">
            <div className="mx-auto sm:max-w-xl md:max-w-2xl lg:max-w-3xl">
              <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                {/* 画像プレースホルダー */}
                <div className="relative w-full aspect-[16/9] bg-gray-100 animate-pulse rounded-t-2xl overflow-hidden" />

                {/* テキストプレースホルダー */}
                <div className="p-6 md:p-8">
                  <div className="h-7 bg-gray-100 rounded w-3/4 mb-4 animate-pulse"></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="h-5 bg-gray-100 rounded w-1/2 animate-pulse"></div>
                    <div className="h-5 bg-gray-100 rounded w-1/3 animate-pulse"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-full animate-pulse"></div>
                    <div className="h-4 bg-gray-100 rounded w-11/12 animate-pulse"></div>
                    <div className="h-4 bg-gray-100 rounded w-10/12 animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">活動が見つかりません</h1>
          <button
            onClick={() => navigate('/#activities')}
            className="btn"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Meta
        title={`${activity.title} | うのつのぶこ`}
        description={activity.content.substring(0, 120)}
        canonical={`https://unotsunobuko.com/activities-detail/${activity.id}`}
        ogTitle={`${activity.title} | うのつのぶこ`}
        ogDescription={activity.content.substring(0, 120)}
        ogImage={activity.imageUrl}
        ogImageAlt={activity.title}
        ogType="article"
        articlePublishedTime={(() => {
          const d = new Date(activity.date);
          return isNaN(d.getTime()) ? activity.date : d.toISOString();
        })()}
        articleModifiedTime={(/* 同日更新を優先 */() => {
          const d = new Date(activity.date);
          return isNaN(d.getTime()) ? activity.date : d.toISOString();
        })()}
        articleAuthor="うのつのぶこ"
        articleSection="活動報告"
        articleTags={[]}
      />
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "ホーム",
                "item": "https://unotsunobuko.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "活動報告",
                "item": "https://unotsunobuko.com/activities-detail"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": activity.title,
                "item": `https://unotsunobuko.com/activities-detail/${activity.id}`
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": activity.title,
            "description": activity.content.substring(0, 120),
            "datePublished": (() => {
              const d = new Date(activity.date);
              return isNaN(d.getTime()) ? activity.date : d.toISOString();
            })(),
            "dateModified": (() => {
              const d = new Date(activity.date);
              return isNaN(d.getTime()) ? activity.date : d.toISOString();
            })(),
            "image": [activity.imageUrl],
            "author": {
              "@type": "Person",
              "name": "うのつのぶこ",
              "url": "https://unotsunobuko.com/"
            },
            "publisher": {
              "@type": "Organization",
              "name": "うのつのぶこ",
              "url": "https://unotsunobuko.com/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://unotsunobuko.com/logo512.png",
                "width": 512,
                "height": 512
              }
            },
            "mainEntityOfPage": `https://unotsunobuko.com/activities-detail/${activity.id}`
          }
        ]}
      />
      {/* Header */}
      <header className="bg-white" style={{ marginTop: '80px' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center text-pink-500 hover:text-pink-600 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold" style={{ color: '#e3628d' }}>
              活動詳細
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Activity Content */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto sm:max-w-xl md:max-w-2xl lg:max-w-3xl">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              {/* 画像 */}
              <div className="relative w-full aspect-[16/9] bg-gray-50 rounded-t-2xl overflow-hidden">
                <img
                  src={activity.imageUrl}
                  alt={activity.title}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-cover rounded-t-2xl"
                />
              </div>
              {/* Divider between image and title */}
              <hr className="border-gray-200" />

              {/* 内容 */}
              <div className="p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                  {activity.title}
                </h1>

                {/* 基本情報 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>{formatDateForDisplay(new Date(activity.date))}</span>
                  </div>
                  {activity.location && (
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-3 flex-shrink-0" />
                      <span>{activity.location}</span>
                    </div>
                  )}
                </div>

                {/* 詳細内容 */}
                <div className="prose max-w-none">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {activity.content}
                  </div>
                </div>

                {/* 前後の記事ナビゲーション */}
                <div className="mt-10 pt-6 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 左: 前の記事 */}
                    <div className="md:col-start-1">
                      {prevActivity && (
                        <button
                          onClick={() => navigate(`/activities-detail/${prevActivity.id}`, { state: (location as any)?.state })}
                          className="group w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-pink-300 hover:shadow-md transition bg-white text-left"
                          aria-label="前の記事へ"
                        >
                          <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-pink-500" />
                          <div className="relative w-28 aspect-[16/9] rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
                            <img src={prevActivity.imageUrl} alt={prevActivity.title} loading="lazy" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <div className="text-[11px] md:text-xs text-gray-500 mb-1">
                              <span className="block">前の記事</span>
                              <span>{formatDateForDisplay(new Date(prevActivity.date))}</span>
                            </div>
                            <div className="font-medium text-gray-800 line-clamp-2 group-hover:text-pink-600">{prevActivity.title}</div>
                          </div>
                        </button>
                      )}
                    </div>

                    {/* 右: 次の記事 */}
                    <div className="md:col-start-2 md:justify-self-end w-full">
                      {nextActivity && (
                        <button
                          onClick={() => navigate(`/activities-detail/${nextActivity.id}`, { state: (location as any)?.state })}
                          className="group w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-pink-300 hover:shadow-md transition bg-white text-left"
                          aria-label="次の記事へ"
                        >
                          <div className="flex-1 hidden md:block">
                            <div className="text-[11px] md:text-xs text-gray-500 mb-1">
                              <span className="block">次の記事</span>
                              <span>{formatDateForDisplay(new Date(nextActivity.date))}</span>
                            </div>
                            <div className="font-medium text-gray-800 line-clamp-2 group-hover:text-pink-600">{nextActivity.title}</div>
                          </div>
                          <div className="relative w-28 aspect-[16/9] rounded-md overflow-hidden bg-gray-50 flex-shrink-0">
                            <img src={nextActivity.imageUrl} alt={nextActivity.title} loading="lazy" className="w-full h-full object-cover" />
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-pink-500" />
                          {/* スモール画面用のタイトル */}
                          <div className="md:hidden flex-1">
                            <div className="text-[11px] md:text-xs text-gray-500 mb-1">
                              <span className="block">次の記事</span>
                              <span>{formatDateForDisplay(new Date(nextActivity.date))}</span>
                            </div>
                            <div className="font-medium text-gray-800 line-clamp-2 group-hover:text-pink-600">{nextActivity.title}</div>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* 戻るボタン */}
                <div className="mt-8 pt-6 flex justify-center">
                  <button
                    onClick={handleBack}
                    className="btn"
                  >
                    活動報告に戻る
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetail; 